import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { Button } from "@shared/ui/button";
import { Label } from "@shared/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useGetAllAssetStatus, useGetAssetType } from "../../hooks";

interface AssetFilters {
  status?: number[];
  type?: string;
  remote_utility_id?: number;
  page?: number;
  limit?: number;
}

interface AssetFilterPanelProps {
  currentFilters: AssetFilters;
  onApplyFilters: (filters: Partial<AssetFilters>) => void;
  onResetFilters: () => void;
}

export const AssetFilterPanel: React.FC<AssetFilterPanelProps> = ({
  currentFilters,
  onApplyFilters,
  onResetFilters,
}) => {
  const remoteUtilityId = useRemoteUtilityId();

  const { data: assetTypesResponse } = useGetAssetType();
  const { data: assetStatusResponse } = useGetAllAssetStatus();

  // Local state for temporary filter values
  const [tempFilters, setTempFilters] = useState<{
    status: number[];
    type: string;
  }>({
    status: currentFilters.status || [],
    type: currentFilters.type || "ALL_TYPES",
  });

  // Convert numeric status to string for form
  const selectedStatusValue =
    tempFilters.status.length > 0
      ? String(tempFilters.status[0])
      : "ALL_STATUS";

  // Initialize form with current filter values
  const form = useForm({
    defaultValues: {
      status: selectedStatusValue,
      type: tempFilters.type || "ALL_TYPES",
    },
  });

  // Update temp filters when current filters change
  useEffect(() => {
    const newTempFilters = {
      status: currentFilters.status || [],
      type: currentFilters.type || "ALL_TYPES",
    };

    setTempFilters(newTempFilters);

    // Update form values
    const newSelectedStatusValue =
      newTempFilters.status.length > 0
        ? String(newTempFilters.status[0])
        : "ALL_STATUS";

    form.setValue("status", newSelectedStatusValue);
    form.setValue("type", newTempFilters.type);
  }, [currentFilters, form]);

  // Update form when tempFilters change
  useEffect(() => {
    const newSelectedStatusValue =
      tempFilters.status.length > 0
        ? String(tempFilters.status[0])
        : "ALL_STATUS";

    form.setValue("status", newSelectedStatusValue);
    form.setValue("type", tempFilters.type);
  }, [tempFilters, form]);

  const handleStatusChange = (value: string) => {
    setTempFilters((prev) => ({
      ...prev,
      status: value === "ALL_STATUS" ? [] : [Number(value)],
    }));
  };

  const handleTypeChange = (value: string) => {
    setTempFilters((prev) => ({
      ...prev,
      type: value === "ALL_TYPES" ? "" : value,
    }));
  };

  const handleApplyFilters = () => {
    const filtersToApply: Partial<AssetFilters> = {};

    if (tempFilters.status.length > 0) {
      filtersToApply.status = tempFilters.status;
    }
    if (tempFilters.type && tempFilters.type !== "ALL_TYPES") {
      filtersToApply.type = tempFilters.type;
    }

    onApplyFilters(filtersToApply);
  };

  const handleResetFilters = () => {
    setTempFilters({
      status: [],
      type: "ALL_TYPES",
    });
    onResetFilters();
  };

  const handleSubmit = (data: any) => {
    handleApplyFilters();
  };

  return (
    <form onSubmit={form.handleSubmit(handleSubmit)}>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label>Asset Status</Label>
          <Controller
            name="status"
            control={form.control}
            render={({ field }) => (
              <Select
                value={selectedStatusValue}
                onValueChange={(value) => {
                  field.onChange(value);
                  handleStatusChange(value);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="ALL_STATUS">All Status</SelectItem>
                    {assetStatusResponse?.map((status: any) => (
                      <SelectItem
                        key={status.key || status.id}
                        value={String(status.key || status.id)}
                      >
                        {status.value || status.name || status.displayName}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div className="space-y-2">
          <Label>Asset Type</Label>
          <Controller
            name="type"
            control={form.control}
            render={({ field }) => (
              <Select
                value={tempFilters.type || "ALL_TYPES"}
                onValueChange={(value) => {
                  field.onChange(value);
                  handleTypeChange(value);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="ALL_TYPES">All Types</SelectItem>
                    {assetTypesResponse?.map((type: any) => (
                      <SelectItem
                        key={type.code || type.id}
                        value={type.code || type.id}
                      >
                        {type.name || type.displayName || type.value}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div className="col-span-1 md:col-span-2 lg:col-span-2 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={handleResetFilters}>
            Reset Filters
          </Button>
          <Button type="submit">Apply Filters</Button>
        </div>
      </div>
    </form>
  );
};
